import React, { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { MenuItemConfig } from 'constants/menu';
import { StaffAction } from 'types/staff';
import ArrowUpIcon from './icons/arrowup-icon';

// Submenu Item Component
interface SubmenuItemProps {
  subItem: MenuItemConfig;
  activeLink: string;
  onUpdateActiveLink: (link: string) => void;
}

const SubmenuItem: React.FC<SubmenuItemProps> = ({ subItem, activeLink, onUpdateActiveLink }) => {
  const linkState = useMemo(() => {
    if (subItem.id === 'edit-profile') {
      return { action: StaffAction.SELF_EDIT };
    }

    if (subItem.id === 'staff-members') {
      return { action: StaffAction.VIEW };
    }

    return subItem.state;
  }, [subItem.state, subItem.id]);

  const isActive = subItem.title === activeLink;

  const handleClick = useCallback(() => {
    onUpdateActiveLink(subItem.title);
  }, [onUpdateActiveLink, subItem.title]);

  return (
    <li
      onClick={handleClick}
      id={`li-${subItem.title.replace(/\s+/g, '')}`}
      data-testid={`li-${subItem.title.replace(/\s+/g, '')}`}
    >
      <Link
        className="linkStyle"
        to={subItem.path || '#'}
        state={linkState}
        id={`link-${subItem.title.replace(/\s+/g, '')}`}
        data-testid={`link-${subItem.title.replace(/\s+/g, '')}`}
      >
        <div
          className={clsx('submenu-item', {
            'submenu-item-active': isActive,
          })}
        >
          <span
            className={clsx('menu-item-text', {
              'submenu-active-text': isActive,
            })}
          >
            {subItem.title}
          </span>
        </div>
      </Link>
    </li>
  );
};

// Submenu Component
interface SubmenuProps {
  submenuItems: MenuItemConfig[];
  activeLink: string;
  onUpdateActiveLink: (link: string) => void;
}

const Submenu: React.FC<SubmenuProps> = ({ submenuItems, activeLink, onUpdateActiveLink }) => {
  const visibleItems = useMemo(
    () => submenuItems.filter((subItem) => !subItem.isHidden),
    [submenuItems]
  );

  return (
    <div className="submenuWrapper">
      <ul className="submenu">
        {visibleItems.map((subItem) => (
          <SubmenuItem
            key={subItem.id}
            subItem={subItem}
            activeLink={activeLink}
            onUpdateActiveLink={onUpdateActiveLink}
          />
        ))}
      </ul>
    </div>
  );
};

interface MenuItemProps {
  item: MenuItemConfig;
  isActive: boolean;
  isMenuCollapsed: boolean;
  menuStates: Record<string, boolean>;
  onToggleState: (key: string) => void;
  onUpdateActiveLink: (link: string) => void;
  isActiveInSubmenu: boolean;
  activeLink: string;
  activeIconColor: string;
  inactiveIconColor: string;
}

const MenuItem: React.FC<MenuItemProps> = ({
  item,
  isActive,
  isMenuCollapsed,
  menuStates,
  onToggleState,
  onUpdateActiveLink,
  isActiveInSubmenu,
  activeLink,
  activeIconColor,
  inactiveIconColor,
}) => {
  const hasSubmenu = item.hasSubmenu && item.submenuItems;

  const handleClick = useCallback(() => {
    if (hasSubmenu && !isMenuCollapsed) {
      onToggleState(item.id);
    } else if (!hasSubmenu) {
      onUpdateActiveLink(item.title);
    }
  }, [hasSubmenu, isMenuCollapsed, onToggleState, item.id, onUpdateActiveLink, item.title]);

  const iconComponent = useMemo(() => {
    if (!item.icon) return null;
    const IconComponent = item.icon;
    const isItemActive = isActive || isActiveInSubmenu;
    return (
      <IconComponent
        color={isItemActive ? activeIconColor : inactiveIconColor}
        opacity={1}
        padding="0"
      />
    );
  }, [activeIconColor, inactiveIconColor, isActive, item.icon, isActiveInSubmenu]);

  const submenuComponent = useMemo(() => {
    if (!hasSubmenu || !item.submenuItems) return null;

    return (
      <Submenu
        submenuItems={item.submenuItems}
        activeLink={activeLink}
        onUpdateActiveLink={onUpdateActiveLink}
      />
    );
  }, [hasSubmenu, item.submenuItems, activeLink, onUpdateActiveLink]);

  const menuItemClasses = useMemo(
    () =>
      clsx('menu-item-text', {
        'submenu-active-text': isActiveInSubmenu,
      }),
    [isActiveInSubmenu]
  );

  const submenuContainerClasses = useMemo(
    () =>
      clsx('submenuContainer', {
        active: isActiveInSubmenu,
      }),
    [isActiveInSubmenu]
  );

  const menuItemContainerClasses = useMemo(
    () =>
      clsx('menu-item-container', {
        'menu-item-active': isActive,
      }),
    [isActive]
  );

  const submenuClasses = useMemo(
    () =>
      clsx('has-submenu', {
        open: menuStates[item.id],
      }),
    [menuStates, item.id]
  );

  if (hasSubmenu) {
    return (
      <li
        className={submenuClasses}
        data-testid={`li-${item.title.replace(/\s+/g, '')}`}
        data-submenu-count={item.submenuItems?.length || 0}
      >
        <div
          className={submenuContainerClasses}
          onClick={handleClick}
          id={`div-${item.title.replace(/\s+/g, '')}`}
          data-testid={`div-${item.title.replace(/\s+/g, '')}`}
        >
          <div className="menu-item">
            {iconComponent}
            <span className={menuItemClasses}>{item.title}</span>
          </div>
          <ArrowUpIcon
            extraClasses="ic"
            color={isActiveInSubmenu ? activeIconColor : inactiveIconColor}
            opacity={1}
            padding="0"
          />
        </div>
        {submenuComponent}
      </li>
    );
  }

  // Regular menu item without submenu
  const menuItemContent = (
    <div className="menu-item">
      {iconComponent}
      <span
        className={clsx('menu-item-text', {
          'submenu-active-text': isActive,
        })}
      >
        {item.title}
      </span>
    </div>
  );

  return (
    <li
      className={menuItemContainerClasses}
      onClick={handleClick}
      id={`li-${item.title.replace(/\s+/g, '')}`}
      data-testid={`li-${item.title.replace(/\s+/g, '')}`}
    >
      {item.path ? (
        <Link
          className="linkStyle"
          to={item.path}
          id={`link-${item.title.replace(/\s+/g, '')}`}
          data-testid={`link-${item.title.replace(/\s+/g, '')}`}
        >
          {menuItemContent}
        </Link>
      ) : (
        menuItemContent
      )}
    </li>
  );
};

export default MenuItem;
